export interface ScopeNode {
  id: number;
  name: string;
  selected: 'yes' | 'no' | 'partial';
  collapsed: boolean;
  children?: ScopeNode[];
  isNew?: boolean;
  assetId?: number;
}

export interface ScopeChip {
  path: string;
  name: string;
}

export class ScopeTreeUtil {
  static readonly SCOPE_DEPTHS_BY_OBJECT = {"buId": 0, "lbuId": 1, "assetId": 2, "siteId": 3};

  static buildScopeTree(branchAssets: any[]): ScopeNode[] {
    const buMap = new Map<string | number, ScopeNode>();

    for (const asset of branchAssets) {
      const buId = asset.bu.id;
      if (!buMap.has(buId)) {
        buMap.set(buId, {
          id: asset.bu.id,
          name: asset.bu.name,
          selected: 'no',
          collapsed: false,
          children: []
        });
      }

      const buNode = buMap.get(buId);

      const lbuName = asset.lbu.name;
      let lbuNode = buNode.children.find(c => c.name == lbuName);
      if (!lbuNode) {
        lbuNode = {id: asset.lbu.id, name: lbuName, selected: 'no', collapsed: false, children: []};
        buNode.children.push(lbuNode);
      }

      if (!lbuNode.children.some(c => c.name == asset.name)) {
        lbuNode.children.push({
          id: asset.id,
          name: asset.name,
          selected: 'no',
          collapsed: false,
          children: (asset.sites ?? []).map((site: any) => ({
            id: site.id,
            type: 'site',
            name: site.name,
            selected: 'no',
            collapsed: false
          }))
        });
      }
    }

    return Array.from(buMap.values());
  }

  static selectScopesInTree(nodes: ScopeNode[], scopes: any[]): void {
    for (const scope of scopes) {
      const notNullKey = Object.keys(scope).find(key => scope[key] != null && Object.keys(ScopeTreeUtil.SCOPE_DEPTHS_BY_OBJECT).includes(key));
      ScopeTreeUtil.selectNodesAtDepth(nodes, scope[notNullKey], ScopeTreeUtil.SCOPE_DEPTHS_BY_OBJECT[notNullKey], 0);
      ScopeTreeUtil.refreshParentStates(nodes);
    }
  }

  static setNodeAndDescendantsSelected(node: ScopeNode, selected: 'yes' | 'no'): void {
    node.selected = selected;
    node.children?.forEach(child => ScopeTreeUtil.setNodeAndDescendantsSelected(child, selected));
  }

  static refreshParentStates(nodes: ScopeNode[]): void {
    for (const node of nodes) {
      if (node.children?.length) {
        ScopeTreeUtil.refreshParentStates(node.children);
        const allYes = node.children.every(c => c.selected === 'yes');
        const allNo = node.children.every(c => c.selected === 'no');
        if (allYes) {
          node.selected = 'yes';
        } else if (allNo) {
          node.selected = 'no';
        } else {
          node.selected = 'partial';
        }
      }
    }
  }

  // Contracts the selection: a fully selected node is returned alone instead of all its descendants
  static computeSelectedChips(nodes: ScopeNode[], ancestors: string[] = [], chips: ScopeChip[] = []): ScopeChip[] {
    for (const node of nodes) {
      if (node.selected === 'yes') {
        chips.push({path: [...ancestors, node.name].join(' - '), name: node.name});
      } else if (node.selected === 'partial' && node.children?.length) {
        ScopeTreeUtil.computeSelectedChips(node.children, [...ancestors, node.name], chips);
      }
    }
    return chips;
  }

  private static selectNodesAtDepth(nodes: ScopeNode[], objectId: number, requiredDepth: number, currentDepth: number): void {
    for (const node of nodes) {
      if (node.id == objectId && currentDepth == requiredDepth) {
        ScopeTreeUtil.setNodeAndDescendantsSelected(node, 'yes');
      }

      if (node.children?.length > 0) {
        ScopeTreeUtil.selectNodesAtDepth(node.children, objectId, requiredDepth, currentDepth + 1);
      }
    }
  }
}
